// apps/web/src/components/landing-v2/MarketingNav.tsx
import { useEffect, useId, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X } from 'lucide-react'

/** Top fixed marketing nav, used by landing v2 + sister pages.
 *  Links: What's inside (anchor on landing), Practitioners (anchor on landing),
 *  Pricing, Help. Right cluster: Sign in, Begin a reading (primary CTA).
 *
 *  Layout breakpoints:
 *  - <lg (≤1023px): wordmark + Begin a reading CTA + hamburger trigger.
 *    Tapping the hamburger opens a quiet drop-down sheet that hosts the
 *    4 nav links and Sign in. Begin a reading stays in the bar so the
 *    primary CTA is always reachable in one tap.
 *  - ≥lg (≥1024px): inline link list, Sign in, Begin a reading. No
 *    hamburger.
 *
 *  The breakpoint moved from md→lg because at exactly 768px all 6 items
 *  (wordmark + 4 links + Sign in + CTA) couldn't fit on one line and the
 *  bar wrapped to two rows.
 *
 *  All cross-route links use <Link> so navigating between marketing surfaces
 *  stays a SPA transition. Anchor targets (#themes, #practitioners) live on
 *  landing v2; from a sister page they navigate back to / with the hash —
 *  harmless if the target section hasn't shipped yet. Plain <Link> over
 *  <NavLink> because we don't render an active state for these items yet.
 *
 *  Hamburger UX is intentionally subtle (slow ease, soft fade + small
 *  translate) per the project's "intimate, considered, slow" north star —
 *  not a snappy SaaS slide. Honors prefers-reduced-motion by jumping
 *  straight to the final state. */
export function MarketingNav() {
  const [open, setOpen] = useState(false)
  const [reducedMotion, setReducedMotion] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const panelId = useId()
  const location = useLocation()

  // Wordmark click: if already on / (no hash), the <Link> would be a no-op,
  // so scroll to top explicitly. Otherwise let <Link> navigate normally —
  // useScrollToHash mounted in MarketingLayout handles scroll-to-top on the
  // cross-route arrival.
  const onWordmarkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (location.pathname === '/' && !location.hash) {
      e.preventDefault()
      window.scrollTo({
        top: 0,
        behavior: reducedMotion ? 'auto' : 'smooth',
      })
    }
  }

  // Track reduced-motion preference reactively so a user toggling system
  // preferences mid-session gets honored without a refresh.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReducedMotion(mql.matches)
    const handler = (e: MediaQueryListEvent) => setReducedMotion(e.matches)
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [])

  // Close the panel automatically when the viewport widens past the lg
  // breakpoint — the inline nav takes over and the panel would otherwise
  // remain "open" in state and trap focus on next narrow resize. Matches
  // Tailwind's lg breakpoint (1024px).
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mql = window.matchMedia('(min-width: 1024px)')
    const handler = (e: MediaQueryListEvent) => {
      if (e.matches) setOpen(false)
    }
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [])

  // Outside-click + Escape to close. Both restore focus to the trigger so
  // keyboard users land back where they started.
  useEffect(() => {
    if (!open) return
    const onPointer = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node | null
      if (!target) return
      if (panelRef.current?.contains(target)) return
      if (buttonRef.current?.contains(target)) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
        buttonRef.current?.focus()
      }
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('touchstart', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('touchstart', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const closePanel = () => setOpen(false)

  return (
    <nav
      aria-label="Primary"
      className="sticky top-0 z-40 border-b border-hairline bg-cosmic-topbar/85 backdrop-blur"
    >
      <div className="mx-auto flex max-w-[1280px] items-center justify-between px-5 py-3 md:px-8">
        {/* Wordmark scales down on the smallest viewports so it fits next to
            the primary CTA + hamburger at 375px without truncation. Tracking
            also tightens a touch — the letterspacing reads as composed at
            18px but eats horizontal space we can't spare at 16px. */}
        <Link
          to="/"
          onClick={onWordmarkClick}
          className="font-display text-base tracking-[0.08em] font-light whitespace-nowrap sm:text-lg sm:tracking-[0.12em]"
        >
          The <span className="text-gold">Cosmic</span> Finder
        </Link>

        {/* Desktop inline nav (≥lg) */}
        <ul className="hidden items-center gap-7 text-sm text-text-secondary lg:flex">
          <li><Link to="/#themes" className="hover:text-text-primary transition-colors">What's inside</Link></li>
          <li><Link to="/#practitioners" className="hover:text-text-primary transition-colors">Creators</Link></li>
          <li><Link to="/pricing" className="hover:text-text-primary transition-colors">Pricing</Link></li>
          <li><Link to="/help" className="hover:text-text-primary transition-colors">Help</Link></li>
        </ul>

        {/* Right cluster. Sign in is hidden <lg and moves into the panel;
            Begin a reading stays visible everywhere because it's the only
            primary CTA. Hamburger trigger renders <lg only. */}
        <div className="flex items-center gap-3">
          <Link
            to="/sign-in"
            className="hidden text-sm text-text-secondary hover:text-text-primary transition-colors lg:inline"
          >
            Sign in
          </Link>
          <Link
            to="/sign-up"
            className="rounded-md bg-gold px-3 py-1.5 text-sm font-medium text-cosmic-black shadow-glow-gold hover:bg-gold-light transition-colors whitespace-nowrap"
          >
            Begin a reading
          </Link>
          <button
            ref={buttonRef}
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            aria-controls={panelId}
            className="-mr-1 inline-flex h-9 w-9 items-center justify-center rounded-md text-text-secondary hover:text-text-primary transition-colors lg:hidden"
          >
            {open ? <X size={20} strokeWidth={1.5} /> : <Menu size={20} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {/* Mobile/tablet drop-down sheet. Always rendered so the open/close
          transition has both states to interpolate, but visually hidden +
          inert when closed. The hidden attribute is intentionally NOT used
          because it blocks transitions; we use opacity + translate +
          pointer-events instead. aria-hidden mirrors the open state for AT. */}
      <div
        ref={panelRef}
        id={panelId}
        role="dialog"
        aria-label="Site menu"
        aria-hidden={!open}
        className={[
          'lg:hidden overflow-hidden border-t border-hairline bg-cosmic-topbar/95 backdrop-blur',
          // Reduced motion: jump cut. Otherwise: slow, considered ease.
          reducedMotion
            ? ''
            : 'transition-[max-height,opacity,transform] duration-500 ease-[cubic-bezier(0.22,0.61,0.36,1)]',
          open
            ? 'max-h-[420px] opacity-100 translate-y-0 pointer-events-auto'
            : 'max-h-0 opacity-0 -translate-y-1 pointer-events-none',
        ].join(' ')}
      >
        <ul className="mx-auto flex max-w-[1280px] flex-col px-5 py-4 md:px-8 text-sm text-text-secondary">
          <li>
            <Link
              to="/#themes"
              onClick={closePanel}
              className="block py-3 hover:text-text-primary transition-colors"
            >
              What's inside
            </Link>
          </li>
          <li>
            <Link
              to="/#practitioners"
              onClick={closePanel}
              className="block py-3 hover:text-text-primary transition-colors"
            >
              Creators
            </Link>
          </li>
          <li>
            <Link
              to="/pricing"
              onClick={closePanel}
              className="block py-3 hover:text-text-primary transition-colors"
            >
              Pricing
            </Link>
          </li>
          <li>
            <Link
              to="/help"
              onClick={closePanel}
              className="block py-3 hover:text-text-primary transition-colors"
            >
              Help
            </Link>
          </li>
          <li className="mt-2 border-t border-hairline pt-3">
            <Link
              to="/sign-in"
              onClick={closePanel}
              className="block py-2 hover:text-text-primary transition-colors"
            >
              Sign in
            </Link>
          </li>
        </ul>
      </div>
    </nav>
  )
}
